import styled from "@emotion/styled";
import { IconInfo, IconPhoneCall } from "@kompare/kompare-ui-library";
import { useEffect, useState } from "react";
import { handleErrorMessage, i18n, showMessage, useCallMe, useGeneralSoContext } from "src/common";
import { CallMeInput } from "src/common/api/apollo/types/types";
import { colors } from "src/theme";
import { Button } from "../buttons";
import { Validation, validationRules } from "../forms";
import { Divider, Flex } from "../grid";
import { InputPhoneNumber } from "../inputs";
import { HtmlText, HtmlTextLink } from "../other";
import { Text } from "../typography";
import IconModalClose from "../icons/closeModal.icon";
import { mediaQuery } from "src/common";

const CallMeModalComponent = styled.div({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  backgroundColor: 'rgba(50, 50, 50, 0.55)',
  transition: 'all 0.5s ease-in-out'
});

const ModalContent = styled.div({
  boxSizing: 'border-box',
  backgroundColor: '#FFF',
  borderRadius: '16px',
  maxHeight: '92%',
  width: 'calc(100% - 40px)',
  maxWidth: '480px',
  position: 'fixed',
  top: '50%',
  left: '50%',
  right: 'auto',
  bottom: 'auto',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
  padding: '24px',
  overflow: 'auto',
  boxShadow: '0 4px 40px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.5s ease-in-out',
  [mediaQuery('tablet')]: {
    padding: '48px',
  }
});

const CallMeModalTitle = styled.div({
  paddingRight: '34px',
  [mediaQuery('tablet')]: {
    paddingRight: '0',
  }
});

const BaseModalClose = styled(IconModalClose)({
  position: 'absolute',
  top: 24,
  right: 24,
  [mediaQuery('tablet')]: {
    top: 30,
    right: 30,
  }
})

export type CallMeModalProps = {
  open: boolean;
  onPhoneChange?: (value: string, isValid: boolean) => void;
  onSend?: () => void;
  onClose?: () => void;
  onThankYou?: () => void;
};

export const CallMeModal: React.FC<CallMeModalProps> = ({ open, onClose, onPhoneChange, onSend, onThankYou }) => {
  const [formVisible, setFormVisible] = useState(true);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
  const [callMeMutation, { loading: callMeMutationLoading }] = useCallMe();
  const { soId } = useGeneralSoContext();

  const initialPhoneNumber = '';
  const initialCountryCode = '+385';

  function getCountryCode(phoneNumber?: string) {
    if (!phoneNumber) {
      return undefined;
    }
    const match = phoneNumber.match(/^\+\d{1,3}/);
    return match ? match[0] : undefined;
  }

  const [phoneNumber, setPhoneNumber] = useState<string>(initialPhoneNumber || '');
  const [countryCode, setCountryCode] = useState<string>(getCountryCode(initialPhoneNumber) || initialCountryCode);

  const validations: Validation[] = [
    { type: 'required', message: i18n.t('components.modals.callMeModal.required') },
    { type: 'validPhoneNumber', message: i18n.t('common.validation.validPhoneNumber'), params: { regionCode: 'countryCode' } },
  ]

  useEffect(() => {
    if (open) {
      const currentOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = currentOverflow;
      };
    }
    return () => { };
  }, [open]);

  useEffect(() => {
    setPhoneNumber(initialPhoneNumber || '');
    setCountryCode(initialCountryCode || getCountryCode(initialPhoneNumber) || '');
  }, [initialPhoneNumber, initialCountryCode]);

  if (!open) {
    return null;
  }

  const validatePhoneNumber = (phone: string, code: string) => {
    let isValid = true;
    let errorMessage;

    for (const validation of validations) {
      let params: { [key: string]: any } | undefined;

      if (validation.params) {
        params = Object.keys(validation.params).reduce((acc, key) => {
          const paramKey = validation.params![key];
          if (paramKey in { phone, code }) {
            acc[key] = (params as any)[paramKey];
          }
          return acc;
        }, {} as { [key: string]: any });
      }

      if (!validationRules[validation.type](phone, params)) {
        isValid = false;
        errorMessage = validation.message;
        break;
      }
    }

    setPhoneError(errorMessage);
    if (onPhoneChange) onPhoneChange(phone, isValid);

    return isValid;
  };

  const handleSend = async () => {
    const isValid = validatePhoneNumber(phoneNumber, countryCode);
    if (!isValid) {
      return;
    }

    const data: CallMeInput = {
      product: 'property-checkout',
      number: phoneNumber,
      soId: soId,
    };

    try {
      await callMeMutation(data);
    } catch (error) {
      // Rate-limit denials are toasted by rateLimitErrorLink; everything else here. The
      // form stays open so the user can retry or call us instead.
      const code = (error as { graphQLErrors?: { extensions?: { code?: unknown } }[] })?.graphQLErrors?.[0]?.extensions?.code;
      if (code !== 'RATE_LIMITED' && code !== 'IP_BLOCKED') {
        showMessage({ type: 'error', message: handleErrorMessage(error), duration: 5000, fixed: true });
      }
      return;
    }

    setFormVisible(false);
    if (onSend) onSend();
  }

  const handlePhoneChange = (value: { phoneNumber: string; countryCode: string; }) => {
    setPhoneNumber(value.phoneNumber);
    setCountryCode(value.countryCode);
    const isValid = validatePhoneNumber(value.phoneNumber, value.countryCode);
    if (onPhoneChange) onPhoneChange(value.phoneNumber, isValid);
  };

  const thankYouButtonClick = () => {
    if (onThankYou) onThankYou();
    window.location.href = 'https://kompare.hr';
  }

  const htmlTextLinks: Array<HtmlTextLink> = [
    { value: 'form', link: 'https://kompare.hr/obrazac-za-prituzbe/', translation: i18n.t('components.modals.callMeModal.htmlTextLinkTranslations.form') },
    { value: 'mail', link: 'mailto:szop@kompare.hr', translation: 'szop@kompare.hr' },
    { value: 'here', link: 'https://kompare.hr/informacije-o-koristenju-osobnih-podataka/', translation: i18n.t('components.modals.callMeModal.htmlTextLinkTranslations.here') },
  ];

  return (
    <CallMeModalComponent id="basicModal">
      <ModalContent>

        <BaseModalClose onClick={onClose} />

        {formVisible &&
          <Flex vertical gap={24}>
            <CallMeModalTitle>
              <Text type='large' bold>{i18n.t('components.modals.callMeModal.title')}</Text>
            </CallMeModalTitle>
            <InputPhoneNumber label={i18n.t('components.modals.callMeModal.phone')} onChange={handlePhoneChange} value={phoneNumber} error={phoneError} />
            <Flex gap={24}>
              <Button text={i18n.t('components.modals.callMeModal.send')} className="btnBlock" onClick={handleSend} disabled={callMeMutationLoading}/>
              <Button text={i18n.t('components.modals.callMeModal.close')} btnType="textBlack" onClick={onClose} />
            </Flex>
            <Divider />
            <Flex gap={8}>
              <IconInfo width={12} height={12} color={colors.icon5} />
              <HtmlText i18nKey="components.modals.callMeModal.gdpr" links={htmlTextLinks} textSize="micro" />
            </Flex>
          </Flex>
        }

        {!formVisible &&
          <Flex vertical gap={24}>
            <CallMeModalTitle>
              <Flex gap={16}>
                <IconPhoneCall color={colors.primary} width={32} height={32} />
                <Text type='large' bold>{i18n.t('components.modals.callMeModal.thankYou')}</Text>
              </Flex>
            </CallMeModalTitle>
            <Text type="medium" bold>{i18n.t('components.modals.callMeModal.contactSoon')}</Text>
            <Text type="base" color={colors.text2}>{i18n.t('components.modals.callMeModal.contactSoon2')}</Text>
            <Button
              text={i18n.t('components.modals.callMeModal.returnToHome')}
              btnType="outlineBlack"
              onClick={thankYouButtonClick} />
          </Flex>
        }

      </ModalContent>
    </CallMeModalComponent>
  );
};
